/**
 * Candela — Quick-Add Modal
 *
 * Opens a centered modal when clicking "+" on product cards.
 * Fetches product data via /products/{handle}.js, shows image + title +
 * description + variant + quantity, with Add to Cart and Favorites.
 *
 * After ATC: updates cart drawer via section rendering, opens drawer.
 */
(function () {
  var modal = document.getElementById('CandelaQuickAdd');
  if (!modal) return;

  var backdrop = modal.querySelector('.candela-quick-add__backdrop');
  var closeBtn = modal.querySelector('.candela-quick-add__close');
  var imgEl = modal.querySelector('.candela-quick-add__img');
  var titleEl = modal.querySelector('.candela-quick-add__title-link');
  var productLinks = modal.querySelectorAll('[data-quick-add-link]');
  var descEl = modal.querySelector('.candela-quick-add__description');
  var optionsEl = modal.querySelector('.candela-quick-add__options');
  var variantWrap = modal.querySelector('.candela-quick-add__zone--mid');
  var qtyInput = modal.querySelector('.candela-quick-add__qty-input');
  var minusBtn = modal.querySelector('[data-action="minus"]');
  var plusBtn = modal.querySelector('[data-action="plus"]');
  var atcBtn = modal.querySelector('.candela-quick-add__atc-btn');
  var favBtn = modal.querySelector('.candela-quick-add__fav-btn');

  var atcText = atcBtn.dataset.addText;
  var currentProduct = null;
  var currentVariant = null;
  var selected = [];
  var swatchMap = {};

  /* Opened from a card: preselect its colour, reuse its swatch colours */
  var openContext = {};

  function open(handle, context) {
    openContext = context || {};
    var controller = new AbortController();
    var timeoutId = setTimeout(function () { controller.abort(); }, 8000);

    fetch('/products/' + handle + '.js', { signal: controller.signal })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (product) {
        currentProduct = product;
        populate(product);
        modal.setAttribute('aria-hidden', 'false');
        if (window.CandelaScrollLock) window.CandelaScrollLock.update();
        closeBtn.focus();
      })
      .catch(function (err) {
        console.error('Quick-add fetch failed:', handle, err);
      })
      .finally(function () {
        clearTimeout(timeoutId);
      });
  }

  function stripHtml(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }

  function populate(product) {
    if (product.featured_image) {
      imgEl.src = product.featured_image;
      imgEl.alt = product.title;
    } else {
      imgEl.src = '';
      imgEl.alt = '';
    }

    titleEl.textContent = product.title;
    setProductLink(null);

    var text = stripHtml(product.body_html || '');
    descEl.textContent = text.length > 120 ? text.substring(0, 120) + '\u2026' : text;

    buildOptions(product);
    qtyInput.value = 1;

    if (window.CandelaFavorites && window.CandelaFavorites.has(product.id)) {
      favBtn.classList.add('is-favorited');
    } else {
      favBtn.classList.remove('is-favorited');
    }
  }

  /* The image and the title open the product page, on the chosen variant once
     one is picked, so the shopper lands on the colour they were looking at. */
  function setProductLink(variant) {
    if (!currentProduct) return;
    var url = currentProduct.url || '/products/' + currentProduct.handle;
    if (variant && variant.id) url += '?variant=' + variant.id;
    productLinks.forEach(function (el) { el.setAttribute('href', url); });
  }

  function updateAtcText(variant) {
    if (!variant || !variant.available) {
      atcBtn.textContent = 'SOLD OUT';
      return;
    }
    atcBtn.textContent = atcText + ' ' + formatMoney(variant.price);
  }

  /* ---------- Options (size / colour) ---------- */

  function isColorOption(name) {
    var n = (name || '').toLowerCase();
    return n.indexOf('colo') !== -1 || n.indexOf('kleur') !== -1;
  }

  function hasRealOptions(product) {
    var v = product.variants[0];
    return !(product.options.length === 1 && v && v.title === 'Default Title');
  }

  function findVariant(product, sel) {
    return product.variants.find(function (v) {
      return v.options.every(function (o, i) { return o === sel[i]; });
    }) || null;
  }

  function valueAvailable(product, index, value) {
    return product.variants.some(function (v) {
      if (!v.available) return false;
      return v.options.every(function (o, i) {
        if (i === index) return o === value;
        return sel_isFree(i) || o === selected[i];
      });
    });
  }

  function sel_isFree(i) {
    return selected[i] === undefined || selected[i] === null;
  }

  function buildOptions(product) {
    optionsEl.innerHTML = '';
    selected = [];
    swatchMap = openContext.swatches || {};

    if (!hasRealOptions(product)) {
      variantWrap.style.display = 'none';
      setVariant(product.variants[0]);
      return;
    }
    variantWrap.style.display = '';

    var options = product.options.map(function (o) { return typeof o === 'object' ? o : { name: o }; });

    /* preselect: colour from the card, then the first available combination */
    var preferred = product.variants.find(function (v) {
      if (openContext.color) {
        var ci = options.findIndex(function (o) { return isColorOption(o.name); });
        if (ci !== -1 && v.options[ci] !== openContext.color) return false;
      }
      return v.available;
    }) || product.variants.find(function (v) { return v.available; }) || product.variants[0];
    selected = preferred.options.slice();

    options.forEach(function (option, index) {
      var group = document.createElement('div');
      group.className = 'candela-quick-add__option';
      var label = document.createElement('span');
      label.className = 'candela-quick-add__option-label';
      var values = option.values || uniqueValues(product, index);
      var isColor = isColorOption(option.name);
      label.textContent = option.name.toUpperCase() + (isColor ? ': ' + selected[index].toUpperCase() : '');
      group.appendChild(label);

      var list = document.createElement('div');
      list.className = 'candela-quick-add__option-values' + (isColor ? ' candela-quick-add__option-values--swatch' : '');
      values.forEach(function (value) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'candela-quick-add__option-btn' + (isColor ? ' candela-quick-add__option-btn--swatch' : '');
        btn.dataset.index = index;
        btn.dataset.value = value;
        if (isColor) {
          btn.style.setProperty('--swatch', swatchMap[value] || '#bdbdbd');
          btn.setAttribute('aria-label', value);
        } else {
          btn.textContent = value;
        }
        btn.addEventListener('click', function () {
          selected[index] = value;
          refreshOptions(product);
        });
        list.appendChild(btn);
      });
      group.appendChild(list);
      optionsEl.appendChild(group);
    });

    refreshOptions(product);
  }

  function uniqueValues(product, index) {
    var seen = [];
    product.variants.forEach(function (v) {
      if (seen.indexOf(v.options[index]) === -1) seen.push(v.options[index]);
    });
    return seen;
  }

  function refreshOptions(product) {
    optionsEl.querySelectorAll('.candela-quick-add__option-btn').forEach(function (btn) {
      var index = parseInt(btn.dataset.index, 10);
      var value = btn.dataset.value;
      var active = selected[index] === value;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      btn.classList.toggle('is-soldout', !valueAvailable(product, index, value));
    });
    optionsEl.querySelectorAll('.candela-quick-add__option').forEach(function (group, index) {
      var name = (product.options[index] && (product.options[index].name || product.options[index])) || '';
      if (isColorOption(name)) {
        group.querySelector('.candela-quick-add__option-label').textContent = name.toUpperCase() + ': ' + (selected[index] || '').toUpperCase();
      }
    });
    setVariant(findVariant(product, selected));
  }

  function setVariant(variant) {
    currentVariant = variant;
    if (variant && variant.featured_image && variant.featured_image.src) {
      imgEl.src = variant.featured_image.src;
    }
    atcBtn.disabled = !(variant && variant.available);
    updateAtcText(variant);
    setProductLink(variant);
  }

  function close() {
    modal.setAttribute('aria-hidden', 'true');
    if (window.CandelaScrollLock) window.CandelaScrollLock.update();
    currentProduct = null;
    currentVariant = null;
  }

  function formatMoney(cents) {
    return '\u20AC' + (cents / 100).toFixed(2).replace('.', ',');
  }

  function updateDrawerDOM(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var source = doc.querySelector('#CartDrawer');
    var target = document.querySelector('#CartDrawer');
    if (!source || !target) return;

    while (target.firstChild) target.removeChild(target.firstChild);
    while (source.firstChild) target.appendChild(source.firstChild);

    var cartEl = target.closest('cart-drawer');
    if (cartEl) cartEl.classList.remove('is-empty');

    var overlay = document.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', function () {
        var d = document.querySelector('cart-drawer');
        if (d) d.close();
      });
    }
  }

  function addToCart() {
    if (!currentProduct) return;
    var variant = currentVariant;
    if (!variant || !variant.available) return;

    var qty = parseInt(qtyInput.value, 10) || 1;

    atcBtn.disabled = true;
    atcBtn.textContent = '\u2026';

    var atcController = new AbortController();
    var atcTimeout = setTimeout(function () { atcController.abort(); }, 8000);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variant.id,
        quantity: qty,
        sections: 'cart-drawer'
      }),
      signal: atcController.signal
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Cart error'); });
        return r.json();
      })
      .then(function (data) {
        close();

        fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
          document.querySelectorAll('.candela-nav__cart-count').forEach(function (el) {
            el.textContent = cart.item_count;
          });
          var bar = document.getElementById('CandelaCartBar');
          if (bar) bar.classList.toggle('is-visible', cart.item_count > 0);
        });

        if (data.sections && data.sections['cart-drawer']) {
          updateDrawerDOM(data.sections['cart-drawer']);
        }

        var isMobile = window.matchMedia('(max-width: 989px)').matches;
        if (!isMobile) {
          var cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        }
      })
      .catch(function (err) {
        console.error('Quick-add ATC:', err);
        atcBtn.textContent = err.name === 'AbortError' ? 'TIMEOUT' : (err.message || 'ERROR');
        setTimeout(function () {
          atcBtn.disabled = false;
          if (currentProduct) updateAtcText(currentVariant);
        }, 3000);
      })
      .finally(function () {
        clearTimeout(atcTimeout);
      });
  }

  /* Quantity controls */
  if (minusBtn) {
    minusBtn.addEventListener('click', function () {
      var val = parseInt(qtyInput.value, 10) || 1;
      if (val > 1) qtyInput.value = val - 1;
    });
  }

  if (plusBtn) {
    plusBtn.addEventListener('click', function () {
      var val = parseInt(qtyInput.value, 10) || 1;
      if (val < 99) qtyInput.value = val + 1;
    });
  }

  /* Event listeners */
  var dialog = modal.querySelector('.candela-quick-add__dialog');

  closeBtn.addEventListener('click', close);
  modal.addEventListener('click', function (e) {
    if (!dialog.contains(e.target)) close();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
      close();
    }
  });

  atcBtn.addEventListener('click', addToCart);

  favBtn.addEventListener('click', function () {
    if (!currentProduct || !window.CandelaFavorites) return;
    var result = window.CandelaFavorites.toggle(currentProduct.id, currentProduct.handle);
    if (result.added) {
      favBtn.classList.add('is-favorited');
    } else {
      favBtn.classList.remove('is-favorited');
    }
  });

  /* Event delegation for quick-add buttons (capture phase to beat Splide) */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.candela-card__quick-add-btn[data-product-handle]');
    if (!btn) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var card = btn.closest('.candela-card');
    var swatches = {};
    if (card) {
      card.querySelectorAll('.candela-card__swatch').forEach(function (sw) {
        swatches[sw.dataset.color] = sw.style.getPropertyValue('--swatch').trim();
      });
    }
    open(btn.dataset.productHandle, { color: btn.dataset.color || null, swatches: swatches });
  }, true);

  window.CandelaQuickAdd = { open: open };
})();
